type RoleTranslator = (
  key: string,
  options?: Record<string, unknown>,
) => string;

type RoleLike = {
  code: string;
  name: string;
  description?: string | null;
  is_system?: boolean;
};

type PermissionLike = {
  code: string;
  module?: string | null;
};

export const BUILT_IN_ROLE_KEYS: Record<string, { name: string; description: string }> = {
  super_admin: {
    name: 'system:accessControl.builtInRoles.super_admin.name',
    description: 'system:accessControl.builtInRoles.super_admin.description',
  },
  admin: {
    name: 'system:accessControl.builtInRoles.admin.name',
    description: 'system:accessControl.builtInRoles.admin.description',
  },
  hr_manager: {
    name: 'system:accessControl.builtInRoles.hr_manager.name',
    description: 'system:accessControl.builtInRoles.hr_manager.description',
  },
  manager: {
    name: 'system:accessControl.builtInRoles.manager.name',
    description: 'system:accessControl.builtInRoles.manager.description',
  },
  worker: {
    name: 'system:accessControl.builtInRoles.worker.name',
    description: 'system:accessControl.builtInRoles.worker.description',
  },
};

export const PERMISSION_MODULE_KEYS: Record<string, string> = {
  organization: 'system:accessControl.permissionModules.organization',
  attendance: 'system:accessControl.permissionModules.attendance',
  processes: 'system:accessControl.permissionModules.processes',
  integrations: 'system:accessControl.permissionModules.integrations',
  system: 'system:accessControl.permissionModules.system',
  other: 'system:accessControl.permissionModules.other',
};

const PERMISSION_RESOURCE_KEYS: Record<string, string> = {
  'organization.profile': 'system:accessControl.permissionResources.organizationProfile',
  'organization.branches': 'system:accessControl.permissionResources.branches',
  'organization.locations': 'system:accessControl.permissionResources.locations',
  'organization.departments': 'system:accessControl.permissionResources.departments',
  'organization.positions': 'system:accessControl.permissionResources.positions',
  'organization.employment_types': 'system:accessControl.permissionResources.employmentTypes',
  'organization.workers': 'system:accessControl.permissionResources.workers',
  'organization.documents': 'system:accessControl.permissionResources.workerDocuments',
  'organization.events': 'system:accessControl.permissionResources.events',
  'organization.announcements': 'system:accessControl.permissionResources.announcements',
  'attendance.rules': 'system:accessControl.permissionResources.attendanceRules',
  'attendance.shifts': 'system:accessControl.permissionResources.shifts',
  'attendance.schedules': 'system:accessControl.permissionResources.schedules',
  'attendance.time_entries': 'system:accessControl.permissionResources.timeEntries',
  'attendance.leave': 'system:accessControl.permissionResources.leave',
  'attendance.approvals': 'system:accessControl.permissionResources.approvals',
  'attendance.geofences': 'system:accessControl.permissionResources.geofences',
  'attendance.kiosk': 'system:accessControl.permissionResources.kioskDevices',
  'attendance.calculation': 'system:accessControl.permissionResources.attendanceCalculation',
  'attendance.reports': 'system:accessControl.permissionResources.attendanceReports',
  'processes.definitions': 'system:accessControl.permissionResources.processDefinitions',
  'processes.instances': 'system:accessControl.permissionResources.processInstances',
  'processes.tasks': 'system:accessControl.permissionResources.processTasks',
  'processes.requests': 'system:accessControl.permissionResources.processRequests',
  'integrations.api_keys': 'system:accessControl.permissionResources.apiKeys',
  'integrations.webhooks': 'system:accessControl.permissionResources.webhooks',
  'integrations.connections': 'system:accessControl.permissionResources.integrationConnections',
  'system.roles': 'system:accessControl.permissionResources.roles',
  'system.workers': 'system:accessControl.permissionResources.systemUsers',
  'system.audit_logs': 'system:accessControl.permissionResources.auditLogs',
  'system.settings': 'system:accessControl.permissionResources.settings',
  'system.sso': 'system:accessControl.permissionResources.ssoConfiguration',
};

const PERMISSION_ACTION_KEYS: Record<string, string> = {
  read: 'system:accessControl.permissionActions.view',
  update: 'system:accessControl.permissionActions.update',
  manage: 'system:accessControl.permissionActions.manage',
  create: 'system:accessControl.permissionActions.create',
  delete: 'system:accessControl.permissionActions.delete',
  run: 'system:accessControl.permissionActions.run',
};

export function getRoleName(role: RoleLike, t: RoleTranslator): string {
  const keys = role.is_system ? BUILT_IN_ROLE_KEYS[role.code] : undefined;
  return keys ? t(keys.name) : role.name;
}

export function getRoleDescription(role: RoleLike, t: RoleTranslator): string {
  const keys = role.is_system ? BUILT_IN_ROLE_KEYS[role.code] : undefined;
  return keys ? t(keys.description) : (role.description || '—');
}

export function getPermissionModuleLabel(module: string | null | undefined, t: RoleTranslator): string {
  return t(PERMISSION_MODULE_KEYS[module || 'other'] || PERMISSION_MODULE_KEYS.other);
}

export function getPermissionDescription(permission: PermissionLike, t: RoleTranslator): string {
  const separator = permission.code.lastIndexOf('.');
  if (separator < 0) return t('system:accessControl.unknownPermission');

  const resourceKey = PERMISSION_RESOURCE_KEYS[permission.code.slice(0, separator)];
  const actionKey = PERMISSION_ACTION_KEYS[permission.code.slice(separator + 1)];
  if (!resourceKey || !actionKey) return t('system:accessControl.unknownPermission');

  return t(actionKey, { resource: t(resourceKey) });
}
